// @ts-nocheck
console.log('🔵 cart-tiered-upsell.js loading...');
import { Component } from '@theme/component';
import { ThemeEvents, CartUpdateEvent, CartAddEvent } from '@theme/events';
console.log('🔵 Imports successful');

/**
 * A custom element that manages tiered cart upsells based on camera quantity.
 * 
 * Tiers:
 * - 1 camera: Unlock 1 free gift (choice of 3)
 * - 2 cameras: Unlock 3 free gifts
 * - 3+ cameras: Unlock a 4th camera for free
 * 
 * @extends {Component}
 */
class CartTieredUpsellComponent extends Component {
  // Configuration - these should match your actual product setup
  static CAMERA_TAGS = ['camera', 'one35', 'flashback-camera'];  // Tags that identify camera products
  static GIFT_TAGS = ['gift', 'free-gift', 'bonus'];  // Tags that identify gift products
  
  // You can also use specific product IDs if tags aren't reliable
  /** @type {string[]} */
  static CAMERA_PRODUCT_IDS = [];  // e.g., ['8002383118530']
  /** @type {string[]} */
  static GIFT_PRODUCT_IDS = [];    // e.g., ['7234567890123']
  
  connectedCallback() {
    super.connectedCallback();
    
    console.log('🚀 Cart tiered upsell component initialized');
    
    // Listen for cart updates
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(CartAddEvent.eventName, this.#handleCartUpdate);
    
    // Use event delegation for clicks and changes
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('change', this.#handleChange);
    
    // Initial load - run immediately since component starts hidden
    console.log('🔄 Starting initial refresh...');
    this.#refreshUpsellContent();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartUpdate);
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('change', this.#handleChange);
  }

  /**
   * Handle all clicks within the component using event delegation
   * @param {Event} event
   */
  #handleClick = (event) => {
    const addButton = /** @type {HTMLElement | null} */ (event.target)?.closest('[data-add-to-cart]');
    if (addButton) {
      event.preventDefault();
      this.#handleAddToCart(addButton);
    }
  }

  /**
   * Handle all change events within the component
   * @param {Event} event
   */
  #handleChange = (event) => {
    const variantSelect = /** @type {HTMLElement | null} */ (event.target)?.closest('[data-variant-select]');
    if (variantSelect) {
      this.#handleVariantChange(variantSelect);
    }
  }

  /**
   * Handle cart update events
   */
  #handleCartUpdate = async () => {
    // Small delay to ensure Shopify has updated the cart on the server
    // This prevents race conditions when fetching cart data
    await new Promise(resolve => setTimeout(resolve, 200));
    
    // Refresh the upsell content when cart changes
    await this.#refreshUpsellContent();
  }

  /**
   * Main logic: Analyze cart and display appropriate upsell content
   */
  async #refreshUpsellContent() {
    console.log('🔄 Refreshing upsell content...');
    try {
      // Add timeout to prevent infinite loading
      const cartPromise = this.#fetchCart();
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Cart fetch timeout')), 5000)
      );
      
      console.log('📡 Fetching cart data...');
      const cart = await Promise.race([cartPromise, timeoutPromise]);
      console.log('📦 Cart fetched. Item count:', cart.item_count);
      
      const analysis = this.#analyzeCart(cart);
      
      console.log('📊 Cart Analysis:', analysis);
      
      // Render the appropriate upsell content based on tier
      this.#renderUpsellContent(analysis);
      
    } catch (error) {
      console.error('❌ Error refreshing upsell content:', error);
      // Show error message instead of hiding
      const contentContainer = this.querySelector('[data-upsell-content]');
      if (contentContainer) {
        contentContainer.innerHTML = '<p class="cart-tiered-upsell__error">Error loading rewards: ' + error.message + '</p>';
      }
      this.style.display = ''; // Show the error
    }
  }

  /**
   * Fetch current cart data with timeout to prevent hanging
   */
  async #fetchCart() {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
    
    try {
      const response = await fetch('/cart.js', { signal: controller.signal });
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`Cart fetch failed: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  /**
   * Analyze cart contents to determine tier and gift status
   * @param {any} cart
   */
  #analyzeCart(cart) {
    /** @type {any[]} */
    const cameras = [];
    /** @type {any[]} */
    const gifts = [];
    /** @type {any[]} */
    const otherItems = [];
    
    // Categorize cart items
    cart.items.forEach(/** @param {any} item */ item => {
      if (this.#isCamera(item)) {
        cameras.push(item);
      } else if (this.#isGift(item)) {
        gifts.push(item);
      } else {
        otherItems.push(item);
      }
    });
    
    const cameraCount = cameras.reduce((sum, item) => sum + item.quantity, 0);
    const giftCount = gifts.reduce((sum, item) => sum + item.quantity, 0);
    
    // Determine tier and what should be shown
    let tier = 0;
    let expectedGifts = 0;
    let message = '';
    let showGifts = false;
    let showCameraUpsell = false;
    
    if (cameraCount >= 3) {
      // Tier 3: 3+ cameras = 4th camera free
      tier = 3;
      expectedGifts = 0; // No gifts at this tier
      
      // Check if they've added the free camera
      if (cameraCount >= 4) {
        message = "All offers have been unlocked!";
        showGifts = false;
        showCameraUpsell = false;
      } else {
        message = `You've got ${cameraCount} ${cameraCount === 1 ? 'camera' : 'cameras'}! Unlock a 4th camera for FREE!`;
        showGifts = false;
        showCameraUpsell = true; // Show the 4th camera with add-to-cart
      }
      
    } else if (cameraCount === 2) {
      // Tier 2: 2 cameras = 3 free gifts
      tier = 2;
      expectedGifts = 3;
      
      if (giftCount >= 3) {
        message = `You have 2 cameras! Add another camera to unlock a FREE 4th camera!`;
        showGifts = false;
        showCameraUpsell = true;
      } else {
        const remaining = 3 - giftCount;
        if (giftCount === 0) {
          message = `You have 2 cameras! Unlock 3 FREE gifts — choose below.`;
        } else {
          message = `You have 2 cameras! Add ${remaining} more gift${remaining > 1 ? 's' : ''} to complete your offer.`;
        }
        showGifts = true;
        showCameraUpsell = false;
      }
      
    } else if (cameraCount === 1) {
      // Tier 1: 1 camera = 1 free gift (choice of 3)
      tier = 1;
      expectedGifts = 1;
      
      if (giftCount >= 1) {
        message = "You added the free gift! Add 1 more camera to unlock 2 more free gifts:";
        showGifts = false;
        showCameraUpsell = true; // Show camera upsell to encourage 2nd camera purchase
      } else {
        message = "You have 1 camera! Unlock a FREE gift — choose one below.";
        showGifts = true;
        showCameraUpsell = false;
      }
      
    } else {
      // No cameras yet - show camera upsell to encourage purchases
      tier = 0;
      message = "Add a camera to unlock FREE gifts!";
      showGifts = false;
      showCameraUpsell = true; // Show camera upsell even with 0 cameras
    }
    
    return {
      tier,
      cameraCount,
      giftCount,
      expectedGifts,
      message,
      showGifts,
      showCameraUpsell,
      cameras,
      gifts,
      cart
    };
  }

  /**
   * Check if an item is a camera (PRIMARY: checks product handle)
   * @param {any} item
   */
  #isCamera(item) {
    // PRIMARY METHOD: Check product handle for specific camera handles
    const handle = item.handle?.toLowerCase() || '';
    const cameraHandles = ['flashback-one35-v2-camera', 'flashback-one35-v1-camera'];
    
    if (cameraHandles.some(cameraHandle => handle.includes(cameraHandle))) {
      return true;
    }
    
    // SECONDARY: Check product title (must contain "one35" to avoid false positives like "camera-lanyard")
    const title = item.product_title?.toLowerCase() || '';
    if (title.includes('one35') && title.includes('camera')) {
      return true;
    }
    
    // FALLBACK: Check by product ID (if configured)
    if (CartTieredUpsellComponent.CAMERA_PRODUCT_IDS.length > 0) {
      if (CartTieredUpsellComponent.CAMERA_PRODUCT_IDS.includes(item.product_id.toString())) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Check if an item is a gift
   * @param {any} item
   */
  #isGift(item) {
    // PRIMARY METHOD: Check by product handle
    const handle = item.handle?.toLowerCase() || '';
    const giftHandles = ['flashback-dad-cap', 'flashback-camera-lanyard', 'premium-vegan-leather-case'];
    
    if (giftHandles.some(giftHandle => handle === giftHandle)) {
      return true;
    }
    
    // SECONDARY METHOD: Check by price - gifts should be free (price = 0)
    // This is reliable as discounts make them $0
    if ((item.price === 0 || item.final_price === 0) && !this.#isCamera(item)) {
      return true;
    }
    
    // TERTIARY: Check if it's a gift product that will become free after discount
    // If original price exists but final price is 0, and it's not a camera, it's a gift
    if (item.original_price > 0 && item.final_price === 0 && !this.#isCamera(item)) {
      return true;
    }
    
    // FALLBACK: Check by product ID (if configured)
    if (CartTieredUpsellComponent.GIFT_PRODUCT_IDS.length > 0) {
      if (CartTieredUpsellComponent.GIFT_PRODUCT_IDS.includes(item.product_id.toString())) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Render the upsell content based on cart analysis
   * @param {any} analysis
   */
  #renderUpsellContent(analysis) {
    console.log('🎯 Rendering upsell. Cameras:', analysis.cameraCount, 'Gifts:', analysis.giftCount, 'showGifts:', analysis.showGifts, 'showCameraUpsell:', analysis.showCameraUpsell);
    
    // Find or create the content container
    let contentContainer = this.querySelector('[data-upsell-content]');
    if (!contentContainer) {
      contentContainer = document.createElement('div');
      contentContainer.setAttribute('data-upsell-content', '');
      this.appendChild(contentContainer);
    }
    
    // Only hide if cart is completely empty (no items at all)
    if (analysis.cart.item_count === 0) {
      console.log('❌ Cart completely empty, hiding component');
      contentContainer.innerHTML = '';
      this.style.display = 'none';
      return;
    }
    
    // If nothing to show, hide the component
    if (!analysis.showGifts && !analysis.showCameraUpsell) {
      if (analysis.message) {
        console.log('✅ Showing success message only');
        contentContainer.innerHTML = `
          <div class="cart-tiered-upsell__message cart-tiered-upsell__message--success">
            <span>${analysis.message}</span>
          </div>
        `;
        this.style.display = '';
      } else {
        console.log('❌ Nothing to show, hiding component');
        // Clear any loading state and hide
        contentContainer.innerHTML = '';
        this.style.display = 'none';
      }
      return;
    }
    
    // Show the component
    console.log('✅ Showing component with products');
    this.style.display = '';
    
    // Build the content HTML
    let html = '';
    
    // Add message
    if (analysis.message) {
      html += `
        <div class="cart-tiered-upsell__message">
          ${analysis.message}
        </div>
      `;
    }
    
    // Add products section with immediate loading state replacement
    html += '<div class="cart-tiered-upsell__products" data-products-container><div class="cart-tiered-upsell__loading">Loading products...</div></div>';
    
    // Replace the initial loading state immediately
    contentContainer.innerHTML = html;
    
    // Fetch and display products (with error handling inside these methods)
    if (analysis.showGifts) {
      console.log('🎁 Loading gift products...');
      this.#loadGiftProducts(analysis);
    } else if (analysis.showCameraUpsell) {
      console.log('📷 Loading camera upsell...');
      this.#loadCameraProducts(analysis);
    }
  }

  /**
   * Load and display gift products
   * @param {any} analysis
   */
  async #loadGiftProducts(analysis) {
    const productsContainer = this.querySelector('[data-products-container]');
    if (!productsContainer) {
      console.error('❌ Products container not found');
      this.style.display = 'none';
      return;
    }
    
    // Get gift products from data attribute or theme settings
    const giftProductHandles = this.dataset.giftProducts ? 
      this.dataset.giftProducts.split(',').map(h => h.trim()).filter(h => h) : 
      [];
    
    console.log('🎁 Loading gift products. Handles:', giftProductHandles);
    
    if (giftProductHandles.length === 0) {
      console.warn('⚠️ No gift products configured in theme settings');
      productsContainer.innerHTML = '<p class="cart-tiered-upsell__empty">No gift products configured. Please contact support.</p>';
      // Don't hide - show the error so it's visible
      return;
    }
    
    try {
      // Add timeout for product fetching
      const fetchPromise = this.#fetchProductsByHandles(giftProductHandles);
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Product fetch timeout')), 8000)
      );
      
      const products = await Promise.race([fetchPromise, timeoutPromise]);
      
      console.log('🎁 Fetched products:', products.length, 'products');
      
      if (products.length === 0) {
        console.warn('⚠️ No gift products found for handles:', giftProductHandles);
        productsContainer.innerHTML = '<p class="cart-tiered-upsell__empty">Gift products temporarily unavailable.</p>';
        // Don't hide - show the error so it's visible
        return;
      }
      
      console.log('✅ Rendering', products.length, 'gift products');
      // Render products - pass true for isGift parameter
      productsContainer.innerHTML = products.map(product => 
        this.#renderProductCard(product, false, true)
      ).join('');
      
    } catch (error) {
      console.error('❌ Error loading gift products:', error);
      productsContainer.innerHTML = '<p class="cart-tiered-upsell__error">Unable to load gifts. Error: ' + error.message + '</p>';
      // Don't hide - show the error so it's visible
    }
  }

  /**
   * Load and display camera products for upsell
   * @param {any} analysis
   */
  async #loadCameraProducts(analysis) {
    const productsContainer = this.querySelector('[data-products-container]');
    if (!productsContainer) {
      console.error('❌ Products container not found');
      this.style.display = 'none';
      return;
    }
    
    // Get camera product handles from data attribute
    const cameraProductHandles = this.dataset.cameraProducts ? 
      this.dataset.cameraProducts.split(',').map(h => h.trim()).filter(h => h) : 
      [];
    
    console.log('📷 Loading camera products. Handles:', cameraProductHandles);
    
    if (cameraProductHandles.length === 0) {
      console.warn('⚠️ No camera products configured in theme settings');
      productsContainer.innerHTML = '<p class="cart-tiered-upsell__empty">No camera products configured. Please contact support.</p>';
      // Don't hide - show the error so it's visible
      return;
    }
    
    try {
      // Add timeout for product fetching
      const fetchPromise = this.#fetchProductsByHandles(cameraProductHandles);
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Product fetch timeout')), 8000)
      );
      
      const products = await Promise.race([fetchPromise, timeoutPromise]);
      
      console.log('📷 Fetched camera products:', products.length, 'products');
      
      // Show first available camera
      const product = products[0];
      if (!product) {
        console.warn('⚠️ No camera products found for handles:', cameraProductHandles);
        productsContainer.innerHTML = '<p class="cart-tiered-upsell__empty">Camera temporarily unavailable.</p>';
        // Don't hide - show the error so it's visible
        return;
      }
      
      // For tier 3 (free camera), we want to show it prominently
      const isFreeCamera = analysis.tier === 3;
      
      console.log('✅ Rendering camera product:', product.title);
      productsContainer.innerHTML = this.#renderProductCard(product, isFreeCamera, false);
      
    } catch (error) {
      console.error('❌ Error loading camera products:', error);
      productsContainer.innerHTML = '<p class="cart-tiered-upsell__error">Unable to load camera. Error: ' + error.message + '</p>';
      // Don't hide - show the error so it's visible
    }
  }

  /**
   * Fetch products by their handles
   * @param {any} handles
   */
  async #fetchProductsByHandles(handles) {
    const products = [];
    
    for (const handle of handles) {
      try {
        const response = await fetch(`/products/${handle.trim()}.js`);
        if (response.ok) {
          const product = await response.json();
          products.push(product);
        }
      } catch (error) {
        console.error(`Error fetching product ${handle}:`, error);
      }
    }
    
    return products;
  }

  /**
   * Render a product card
   * @param {any} product
   * @param {boolean} isFree - Whether to show as free (for 4th camera)
   * @param {boolean} isGift - Whether this is a gift product (shows strikethrough price)
   */
  /**
   * Get first available variant, fallback to first variant
   * @param {any} product
   */
  #getInitialVariant(product) {
    if (!product?.variants?.length) return null;
    return product.variants.find(variant => variant.available) || product.variants[0];
  }
  #renderProductCard(product, isFree = false, isGift = false) {
    const variant = this.#getInitialVariant(product);
    if (!variant) return '';
    let priceHtml = '';
    
    if (isFree || isGift) {
      // Show strikethrough original price + FREE
      const originalPrice = this.#formatMoney(variant.price);
      priceHtml = `
        <span class="cart-tiered-upsell__product-price--original" style="text-decoration: line-through; opacity: 1; margin-right: 4px; color: #888888; font-weight: 400;">${originalPrice}</span>
        <span class="cart-tiered-upsell__product-price--free" style="color: #16a34a; font-weight: 600;">FREE</span>
      `;
    } else {
      priceHtml = this.#formatMoney(variant.price);
    }
    
    return `
      <div class="cart-tiered-upsell__product" data-product-id="${product.id}">
        <div class="cart-tiered-upsell__product-image">
          ${product.featured_image ? `
            <img 
              src="${product.featured_image}" 
              alt="${this.#escapeHtml(product.title)}"
              loading="lazy"
              class="cart-tiered-upsell__product-image-img"
            >
          ` : `
            <div class="cart-tiered-upsell__product-image-placeholder">
              <svg viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" stroke-width="2"/>
                <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                <path d="M21 15l-5-5L5 21" stroke="currentColor" stroke-width="2"/>
              </svg>
            </div>
          `}
          ${(isFree || isGift) ? '<div class="cart-tiered-upsell__product-badge">FREE</div>' : ''}
        </div>
        
        <div class="cart-tiered-upsell__product-info">
          <h4 class="cart-tiered-upsell__product-title">${product.title}</h4>
          <div class="cart-tiered-upsell__product-price">
            ${priceHtml}
          </div>
          
          ${product.variants.length > 1 ? `
            <select 
              class="cart-tiered-upsell__variant-select" 
              data-variant-select
              data-product-id="${product.id}"
            >
              ${product.variants.map(/** @param {any} v */ v => `
                <option 
                  value="${v.id}"
                  data-image="${v.featured_image ? v.featured_image.src : ''}"
                  ${!v.available ? 'disabled' : ''}
                >
                  ${v.title}${!v.available ? ' - Sold Out' : ''}
                </option>
              `).join('')}
            </select>
          ` : ''}
        </div>
        
        <div class="cart-tiered-upsell__product-actions">
          <button 
            class="cart-tiered-upsell__add-button"
            data-add-to-cart
            data-product-id="${product.id}"
            data-variant-id="${variant.id}"
            ${!variant.available ? 'disabled' : ''}
          >
            <svg class="cart-tiered-upsell__add-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M12 5v14m-7-7h14" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <span class="cart-tiered-upsell__add-text">Add</span>
            <span class="cart-tiered-upsell__loading-icon" style="display: none;">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" opacity="0.3"/>
                <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                  <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
                </path>
              </svg>
            </span>
            <span class="cart-tiered-upsell__success-icon" style="display: none;">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </button>
        </div>
      </div>
    `;
  }

  /**
   * Handle variant selection change
   * @param {any} select
   */
  #handleVariantChange(select) {
    const variantId = select.value;
    const productElement = select.closest('.cart-tiered-upsell__product');
    const addButton = productElement?.querySelector('[data-add-to-cart]');
    
    if (addButton) {
      addButton.dataset.variantId = variantId;
      
      // Check if variant is available
      const selectedOption = select.options[select.selectedIndex];
      const isAvailable = !selectedOption.disabled;
      addButton.disabled = !isAvailable;
    }
    
    // Update product image if variant has its own image
    const selectedOption = select.options[select.selectedIndex];
    const variantImage = selectedOption.dataset.image;
    
    if (variantImage && productElement) {
      const imageElement = productElement.querySelector('.cart-tiered-upsell__product-image img');
      if (imageElement) {
        // Fade out, change image, fade in
        imageElement.style.opacity = '0.5';
        imageElement.style.transition = 'opacity 0.2s ease';
        
        setTimeout(() => {
          imageElement.src = variantImage;
          imageElement.style.opacity = '1';
        }, 200);
      }
    }
  }

  /**
   * Handle add to cart button click
   * @param {any} button
   */
  async #handleAddToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId) return;
    
    const addIcon = button.querySelector('.cart-tiered-upsell__add-icon');
    const addText = button.querySelector('.cart-tiered-upsell__add-text');
    const loadingIcon = button.querySelector('.cart-tiered-upsell__loading-icon');
    const successIcon = button.querySelector('.cart-tiered-upsell__success-icon');
    
    // Show loading state
    button.disabled = true;
    if (addIcon) addIcon.style.display = 'none';
    if (addText) addText.style.display = 'none';
    if (loadingIcon) loadingIcon.style.display = 'block';
    
    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (data.status || !response.ok) {
        throw new Error(data.message || 'Failed to add to cart');
      }
      
      // Show success state
      if (loadingIcon) loadingIcon.style.display = 'none';
      if (successIcon) successIcon.style.display = 'block';
      
      // Trigger cart refresh - this will automatically update both the cart drawer and this component
      const cartAddEvent = new CartAddEvent(data, this.id, {
        source: 'cart-tiered-upsell',
        variantId: variantId
      });
      document.dispatchEvent(cartAddEvent);
      
      // Reset button after delay
      setTimeout(() => {
        button.disabled = false;
        if (addIcon) addIcon.style.display = 'block';
        if (addText) addText.style.display = 'block';
        if (loadingIcon) loadingIcon.style.display = 'none';
        if (successIcon) successIcon.style.display = 'none';
      }, 1500);
      
    } catch (error) {
      console.error('Error adding to cart:', error);
      
      // Show error state
      if (loadingIcon) loadingIcon.style.display = 'none';
      if (addIcon) addIcon.style.display = 'block';
      if (addText) {
        const originalText = addText.textContent;
        addText.textContent = 'Error';
        setTimeout(() => {
          addText.textContent = originalText;
        }, 2000);
        addText.style.display = 'block';
      }
      
      button.disabled = false;
    }
  }

  /**
   * Format money (simple version - adjust based on your currency settings)
   * @param {any} cents
   */
  #formatMoney(cents) {
    const amount = parseFloat(cents) / 100;
    return `$${amount.toFixed(2)}`;
  }

  /**
   * Escape HTML to prevent XSS
   * @param {any} text
   */
  #escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

if (!customElements.get('cart-tiered-upsell-component')) {
  customElements.define('cart-tiered-upsell-component', CartTieredUpsellComponent);
  console.log('✅ cart-tiered-upsell-component registered');
} else {
  console.log('ℹ️ cart-tiered-upsell-component already registered');
}

